import axios from 'axios'

// El backend de Finpro detecta el portal de origen a partir del header
// `Origin` que setea el browser automáticamente cuando `withCredentials: true`.
// No hace falta mandar un header custom (`X-Portal`); de hecho Finpro pidió
// explícitamente que no se mande nada extra y se confíe en `Origin`.
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor de REQUEST - Verificar tiempo de expiración antes de hacer request
api.interceptors.request.use(
  config => {
    // Solo verificar para endpoints protegidos (no para login, check, etc)
    const protectedEndpoints = ['/accounts', '/transfer', '/cvu', '/system/ready', '/notifications']
    const isProtected = protectedEndpoints.some(endpoint => config.url?.includes(endpoint))
    
    if (isProtected) {
      const tokenExpiresAt = localStorage.getItem('token_expires_at')
      
      if (tokenExpiresAt) {
        const expiresAt = parseInt(tokenExpiresAt)
        const now = Date.now()
        const secondsLeft = Math.floor((expiresAt - now) / 1000)
        
        console.log(`⏰ Token expira en ${secondsLeft} segundos`)
        
        // Si quedan menos de 20 segundos, forzar re-login
        if (secondsLeft < 20) {
          console.warn('⚠️ Token por expirar (< 20 segundos) - Forzando re-login')
          
          // Guardar ruta actual
          const currentPath = window.location.pathname
          localStorage.setItem('return_path', currentPath)
          
          // Limpiar token
          localStorage.removeItem('token_expires_at')
          
          // Redirigir a login
          window.location.href = '/login'
          
          // Cancelar el request
          return Promise.reject({
            message: 'Token por expirar - Re-autenticación requerida',
            code: 'TOKEN_EXPIRING'
          })
        }
      }
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Interceptor de RESPONSE - Manejar errores de autenticación
api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status
    const code = error.response?.data?.code
    const url = error.config?.url || ''

    // Los endpoints de auth manejan sus propios 401 (login con credenciales
    // incorrectas, check de sesión, etc.). No los interceptamos acá para no
    // pisar sus mensajes.
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/check')

    // Cualquier 401 en un endpoint protegido significa sesión vencida o sin
    // token (el backend devuelve TOKEN_EXPIRED o NO_TOKEN / "Authentication
    // required"). En todos los casos limpiamos la sesión local y mandamos al
    // login para que el usuario vuelva a autenticarse, en vez de mostrar el
    // error crudo.
    if (status === 401 && !isAuthEndpoint) {
      try {
        const currentPath = window.location.pathname
        if (currentPath !== '/login') {
          localStorage.setItem('return_path', currentPath)
        }
        localStorage.removeItem('token_expires_at')
        localStorage.removeItem('user_role')
        localStorage.removeItem('username')
        localStorage.removeItem('is_portal_user')
        localStorage.removeItem('is_operator')
        localStorage.removeItem('assigned_accounts')
        localStorage.removeItem('portal_role')
        localStorage.setItem(
          'login_blocked_message',
          'Tu sesión expiró. Por favor, iniciá sesión nuevamente.'
        )
      } catch (_) { /* localStorage puede fallar en modo incógnito; lo ignoramos */ }

      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    // El backend rechazó porque el user pertenece a otro portal (portal_tag
    // distinto al dominio actual). Limpiamos sesión local y mandamos al
    // login con un mensaje GENÉRICO de credenciales para no filtrar que el
    // usuario existe en otro portal.
    if (status === 403 && code === 'PORTAL_MISMATCH') {
      try {
        localStorage.removeItem('token_expires_at')
        localStorage.removeItem('user_role')
        localStorage.removeItem('username')
        localStorage.removeItem('is_portal_user')
        localStorage.removeItem('is_operator')
        localStorage.removeItem('assigned_accounts')
        localStorage.removeItem('portal_role')

        localStorage.setItem(
          'login_blocked_message',
          'Usuario o contraseña incorrectos'
        )
      } catch (_) { /* localStorage puede fallar en modo incógnito; lo ignoramos */ }

      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export default api

